import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { isEmpty, concat } from 'lodash';
/* The option SHAPE stays `react-multi-select-component`'s `{ label, value }` — it is what
   `CostingToDoColumns` holds in localStorage and what `readSelectedColumnOptions` parses — but the
   control itself is now the master-design dropdown. Keeping the type avoids migrating stored
   selections for a purely visual change. */
import type { Option } from 'react-multi-select-component';
import { NewSearch } from '../../../../Components/PIM/common/NewSearch';
import { NewSelectMultiSelect } from '../../../../Components/PIM/common/NewSelectMultiSelect';
import { exportExcel, exportToPdf, filterSortData, getCostingList, getCostingStats, setCostingSelectedIds } from './store/Costing.action';
import { getCostingStageList } from '../../../SystemMasters/Store/SystemMasters.action';
import CostingTODOTable from './CostingTODOTable';
import { Page_Size } from '../../../../Components/helper';
import ProductCostingFilter from './ProductCostingFilter';
import ProductCostingTabMenu from '../../../../Components/GreedyNavBar/ProductCostingTabMenu';
import { getCostingTemplateList } from '../../../Library/CostingTemplateTSX/store/costingTemplate.actions';
import { readSelectedColumnOptions, resolveCostingInfoLabel } from './costing-todo.helpers';
import type { CostingDispatch, CostingFilterValues, CostingListParams, CostingRecord, CostingRootState } from './store/Costing.type';
import './costing-todo-titlebar.css';
import { discardStaleCostingFilter } from '../../../../Components/helper';

interface CostingPageState {
  costingTodoList: CostingRecord[];
  isLoading: boolean;
  hasMore: boolean;
  loading: boolean;
  totalRecords: number | null;
  searchLoaded: boolean;
  isExportLoading: boolean;
  isDownloadLoading: boolean;
  selectedCount: number;
  selectedIds: string[];
  costingCommentListingModal: boolean;
  costingCommentList: unknown[];
  selectedCostingTemplate: string | null;
  selectedColumns: Option[];
  searchText: string;
  selectedCostingWithDevids: Array<{ costingId?: string | undefined; devOrderId?: string | undefined }>;
  isTabLoading: boolean;
  isInitialLoad: boolean;
}

/** Costing TODO page: template tabs, column chooser, debounced search and bulk comment. */
function Costing() {
  const dispatch = useDispatch<CostingDispatch>();
  const { t: translate } = useTranslation('common');
  const t = translate as unknown as TranslateFn;
  const selectedTemplateRef = useRef<string | null>(null);

  const reducers = useSelector((redState: CostingRootState) => redState);
  const {
    CostingTodoListData: {
      costingListData,
      costingFilterValues,
      costingFilterValues: {
        searchText: reduxSearchText,
        sortOrder,
        sortPropertyName,
        supplier,
        colourway,
        currency,
        incoterms,
        selectedcosting_Status,
        pageSize,
        selectedBrand,
        selectedSubBrand,
        selectedSeason,
        selectedYear,
        selectedMonth,
        selectedProductType,
        selectedCat1,
        selectedCat2,
        selectedCat3,
        selectedCat4,
        selectedCompanyUser,
        selectedDesigner,
        selectedCoo,
        productGroupId,
      },
      costingListTotalCount,
      costingSelectedIds,
    },
    UserDetails: { userInfo, isSupplierLoggedIn },
    CostingTemplateData: { costingTemplateList },
    SystemMastersDetails: { subBrandAccess },
  } = reducers;

  const [state, setState] = useState<CostingPageState>({
    costingTodoList: [],
    isLoading: false,
    hasMore: false,
    loading: true,
    totalRecords: null,
    searchLoaded: false,
    isExportLoading: false,
    isDownloadLoading: false,
    selectedCount: 0,
    selectedIds: [],
    costingCommentListingModal: false,
    costingCommentList: [],
    selectedCostingTemplate: null,
    selectedColumns: [],
    searchText: '',
    selectedCostingWithDevids: [],
    isTabLoading: false,
    isInitialLoad: true,
  });

  const { selectedCostingTemplate, selectedColumns } = state;
  const localStorageSelectedColumns = readSelectedColumnOptions() as Option[];

  useEffect(() => {
    if ((costingTemplateList?.length ?? 0) > 0) {
      setState((prev) => ({ ...prev, selectedCostingTemplate: costingTemplateList?.[0]?._id ?? null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costingTemplateList?.length]);

  useEffect(() => {
    const callFn = async () => {
      const params = {
        isAllRecord: true,
      };
      dispatch(getCostingTemplateList(params));
    };
    callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (localStorageSelectedColumns?.length) {
      setState((prev) => ({
        ...prev,
        selectedColumns: localStorageSelectedColumns,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      selectedCount: costingSelectedIds?.length || 0,
      selectedIds: costingSelectedIds || [],
    }));

    if (!costingSelectedIds?.length) {
      setState((prev) => ({
        ...prev,
        selectedCostingWithDevids: [],
      }));
    }
  }, [costingSelectedIds]);

  /** Persists the chosen column set so the table can read it back on every render. */
  const handleSelectedColumns = (nextSelectedColumns: Option[]) => {
    setState((prev) => ({ ...prev, selectedColumns: nextSelectedColumns }));
    localStorage.setItem('CostingToDoColumns', JSON.stringify(nextSelectedColumns));
  };

  useEffect(() => {
    selectedTemplateRef.current = selectedCostingTemplate;
  }, [selectedCostingTemplate]);

  useEffect(() => {
    const loadCostingStages = async () => {
      if (!isEmpty(userInfo.userId)) {
        await dispatch(
          getCostingStageList({
            loggedInUserId: userInfo.userId,
          }),
        );
      }
    };
    loadCostingStages();
  }, [dispatch, userInfo.userId]);

  useEffect(() => {
    const callFn = async () => {
      let filterd: Partial<CostingListParams> = {};
      /* BM-9901: discard a blob saved before the costing-stage filter was removed from this page. */
      discardStaleCostingFilter('costingTodoListFilter');
      const obj = JSON.parse(localStorage.getItem('costingTodoListFilter') ?? 'null') as (Partial<CostingFilterValues> & { pageIndex?: number }) | null;
      if (obj) {
        filterd = {
          supplier: obj.supplier,
          colourway: obj.colourway,
          currency: obj.currency,
          incoterms: obj.incoterms,
          status: Array.isArray(obj.selectedcosting_Status) ? obj.selectedcosting_Status : obj.selectedcosting_Status ? [obj.selectedcosting_Status] : [],
          brand: obj.selectedBrand,
          season: obj.selectedSeason,
          year: obj.selectedYear,
          month: obj.selectedMonth,
          productType: obj.selectedProductType,
          catOne: obj.selectedCat1,
          catTwo: obj.selectedCat2,
          catThree: obj.selectedCat3,
          catFour: obj.selectedCat4,
          createdBy: obj.selectedCompanyUser,
          designer: obj.selectedDesigner,
          COO: obj.selectedCoo,
          productGroupId: obj.productGroupId,
          ...(subBrandAccess ? { subBrand: obj.selectedSubBrand } : {}),
          licensorId: obj.selectedLicensor,
          licenseId: obj.selectedLicense,
          likenessId: obj.selectedLikeness,
        };
        obj.pageIndex = 1;
        await dispatch(filterSortData(obj));
      }

      const currentSearchText = reduxSearchText || '';

      const params: CostingListParams = {
        pageSize,
        pageIndex: 1,
        type: 'costing',
        searchText: currentSearchText,
        ...filterd,
        supplier: isSupplierLoggedIn ? [userInfo.userId as string] : obj?.supplier || [],
        costingTemplateId: selectedCostingTemplate,
      };
      setState((prevState) => ({ ...prevState, isLoading: true }));

      const res = await dispatch(getCostingList(params));
      if (res) {
        setState((prevState) => ({
          ...prevState,
          costingTodoList: res,
          hasMore: true,
          isLoading: false,
        }));
      }
      dispatch(getCostingStats(params));
    };
    if (selectedCostingTemplate && state.isInitialLoad) {
      callFn();
      setState((prev) => ({ ...prev, isInitialLoad: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedCostingTemplate]);

  /* Mount-only debounce whose body never runs: searchLoaded is false at mount and the dep array
     is [dispatch]. Preserved from the original; the live search is the effect further below. */
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (state?.searchLoaded && selectedCostingTemplate) {
        const params: CostingListParams = {
          pageIndex: 1,
          pageSize: Page_Size,
          status: Array.isArray(selectedcosting_Status) ? selectedcosting_Status : selectedcosting_Status ? [selectedcosting_Status] : [],
          sortOrder,
          sortPropertyName,
          supplier,
          colourway,
          currency,
          incoterms,
          brand: selectedBrand,
          type: 'costing',
          costingTemplateId: selectedCostingTemplate,
        };
        await dispatch(getCostingList(params));
        setState((searchEndState) => ({
          ...searchEndState,
          isLoading: false,
          searchLoaded: false,
        }));
      }
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (costingListTotalCount > (costingListData?.length ?? 0)) {
      setState((inInfinitestate) => ({
        ...inInfinitestate,
        hasMore: true,
      }));
    } else
      setState((inInfinitestate) => ({
        ...inInfinitestate,
        hasMore: false,
      }));
  }, [costingListData?.length, costingListTotalCount]);

  /** Server-side export. Unreachable today — see IMPLEMENTATION_NOTES.md D1/D2. */
  const exportFiles = async (type: string) => {
    const params: CostingListParams = {
      status: selectedcosting_Status,
      sortOrder,
      sortPropertyName,
      supplier,
      colourway,
      currency,
      incoterms,
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'costing',
      costingIds: costingSelectedIds,
      downloadType: type,
      brand: selectedBrand,
    };

    if (type === 'pdf') {
      setState((prevState) => ({ ...prevState, isDownloadLoading: true }));
      await dispatch(exportToPdf(params));
      setState((prevState) => ({ ...prevState, isDownloadLoading: false }));
    } else {
      setState((prevState) => ({ ...prevState, isExportLoading: true }));
      await dispatch(exportExcel(params));
      setState((prevState) => ({ ...prevState, isExportLoading: false }));
    }
  };

  const getCount = (selectedCnt: string[]) => {
    setState((prevState) => ({
      ...prevState,
      selectedCount: selectedCnt.length,
      selectedIds: selectedCnt,
    }));
  };

  const getSelectedIdsWithDevIds = (selectedCostingWithDevids: Array<{ costingId?: string | undefined; devOrderId?: string | undefined }>) => {
    setState((prevState) => ({
      ...prevState,
      selectedCostingWithDevids,
    }));
  };

  const costingTemplateListOptions = useMemo(() => {
    return costingTemplateList?.map((item) => {
      return {
        eventKey: item?._id,
        eventName: item?.name,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costingTemplateList?.length]);

  /**
   * Activates a costing template tab and loads its data. On the initial load the tab menu
   * auto-selects the first tab through this callback; in that case only the selected template
   * is recorded so the initial-load effect owns the single filter-aware fetch, preventing a
   * duplicate, filter-less getCostingList/getCostingStats pair.
   */
  const activeTheTab = async (tabName: string, module: 'selectedCostingTemplate') => {
    setState((prev) => ({ ...prev, [module]: tabName, isTabLoading: true }));
    await dispatch(filterSortData({ pageIndex: 1, pageSize }));

    const currentSearchText = state.searchText || reduxSearchText || '';

    const params: CostingListParams = {
      pageSize,
      pageIndex: 1,
      type: 'costing',
      searchText: currentSearchText,
      supplier: isSupplierLoggedIn ? [userInfo.userId as string] : supplier || [],
      colourway,
      currency,
      incoterms,
      status: selectedcosting_Status,
      sortOrder,
      sortPropertyName,
      brand: selectedBrand,
      season: selectedSeason,
      year: selectedYear,
      month: selectedMonth,
      productType: selectedProductType,
      catOne: selectedCat1,
      catTwo: selectedCat2,
      catThree: selectedCat3,
      catFour: selectedCat4,
      createdBy: selectedCompanyUser,
      designer: selectedDesigner,
      COO: selectedCoo,
      productGroupId,
      ...(subBrandAccess ? { subBrand: selectedSubBrand } : {}),
      costingTemplateId: tabName,
    };

    try {
      const res = await dispatch(getCostingList(params));
      if (res) {
        setState((prevState) => ({
          ...prevState,
          costingTodoList: res,
          hasMore: true,
          isTabLoading: false,
        }));
      }
      dispatch(getCostingStats(params));
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        isTabLoading: false,
      }));
    }
  };

  /**
   * Re-reads the header stat badges after inline edits are saved. The badges aggregate the filtered
   * set, so an edit to any costing changes them — the ticket asks for calculations to reflect on
   * screen without a refresh, and these are calculations too.
   */
  const refreshCostingStats = useCallback(() => {
    if (!selectedCostingTemplate) return;
    const params = {
      ...costingFilterValues,
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'costing',
      searchText: state.searchText,
      costingTemplateId: selectedCostingTemplate,
      ...(isSupplierLoggedIn ? { supplierId: userInfo.userId } : {}),
    } as CostingListParams;
    dispatch(getCostingStats(params));
  }, [dispatch, selectedCostingTemplate, costingFilterValues, state.searchText, isSupplierLoggedIn, userInfo.userId]);

  /** Options for the column chooser, with labels aliased from the template's costingInfo. */
  const activeCostingtemplateColumns = useMemo<Option[]>(() => {
    const findTheActiveCosting = costingTemplateList?.find((vl) => vl?._id === selectedCostingTemplate);
    const costingInfoDefaults = findTheActiveCosting?.isCostingInfoActive ? (findTheActiveCosting?.costingInfo || []).filter((ci) => ci?.isDefault) : [];
    /* Canonical slot index, not the stored `position` — the chooser had the same off-by-one as the
       grid headers, so a reordered template mislabelled both. See `resolveCostingInfoLabel`. */
    const labelFor = (slot: number, fallback: string): string =>
      resolveCostingInfoLabel(costingInfoDefaults, slot, fallback, Boolean(findTheActiveCosting?.isCostingInfoActive));
    let columnsOptions: Option[] = [
      { label: labelFor(0, 'Reference'), value: 'reference' },
      { label: labelFor(9, 'Sample Ref'), value: 'sampleRef' },
      { label: labelFor(1, 'Supplier'), value: 'supplier' },
      { label: labelFor(2, 'Factory'), value: 'factory' },
      { label: labelFor(5, 'MOQ Needed'), value: 'moqNeeded' },
      { label: 'Colourway', value: 'colourway' },
      { label: labelFor(7, 'Incoterms'), value: 'incoterm' },
      { label: 'Size', value: 'size' },
      { label: 'Length', value: 'length' },
      { label: labelFor(8, 'Shipment Method'), value: 'shipmentMethod' },
      { label: labelFor(4, 'Currency'), value: 'currency' },
      { label: labelFor(6, 'Target Cost'), value: 'targetCost' },
      { label: 'Quantity', value: 'quantity' },
      { label: 'Price Per Unit', value: 'pricePerUnit' },
      { label: 'Price Per Unit (Incoterms)', value: 'pricePerUnitIncoterm' },
      { label: 'Total (Incoterms)', value: 'totalIncoterm' },
    ];
    if (findTheActiveCosting && !isSupplierLoggedIn) {
      if (findTheActiveCosting?.topColumnArr) {
        columnsOptions = concat(
          columnsOptions,
          findTheActiveCosting?.topColumnArr
            ?.filter((vl) => vl?.isActive)
            ?.map((val) => {
              return { label: val.aliasDisplayName || val.title, value: val.aliasName };
            }),
        );
      }
      if (findTheActiveCosting?.defaultColumns) {
        columnsOptions = concat(
          columnsOptions,
          findTheActiveCosting?.defaultColumns
            ?.filter((vl) => vl?.isActive)
            ?.map((val) => {
              return { label: val.aliasDisplayName || val.title, value: val.aliasName };
            }),
        );
      }
      if (findTheActiveCosting?.bottomColumnArr) {
        columnsOptions = concat(
          columnsOptions,
          findTheActiveCosting?.bottomColumnArr
            ?.filter((vl) => vl?.isActive)
            ?.map((val) => {
              return { label: val.aliasDisplayName || val.title, value: val.aliasName };
            }),
        );
      }
    }
    return columnsOptions || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCostingTemplate, costingTemplateList?.length]);

  /**
   * The chooser's selected chips, relabelled from the live option list.
   *
   * `handleSelectedColumns` persists whole `{ label, value }` objects, so a selection saved while
   * the label resolver was keyed on `position` still carries the wrong text — a Currency chip
   * reading "Costing Template". react-select renders chips from the value objects, not from
   * `options`, so the stale label would survive the resolver fix until the user re-picked the
   * column. Matching each stored value against the freshly-built options makes the label
   * self-healing; a value the active template no longer offers is passed through untouched rather
   * than silently dropped.
   */
  const selectedColumnOptions = useMemo<Option[]>(() => {
    const optionByValue = new Map(activeCostingtemplateColumns.map((option) => [option.value, option]));
    return (selectedColumns ?? []).map((chosen) => optionByValue.get(chosen?.value) ?? chosen);
  }, [selectedColumns, activeCostingtemplateColumns]);

  /**
   * Records a search keystroke. Takes the value rather than the event because `NewSearch` owns its
   * own input and reports the text; the debounce that turns this into a request is unchanged.
   */
  const handleSearch = (nextSearchText: string) => {
    setState((prevState) => ({
      ...prevState,
      searchLoaded: true,
      searchText: nextSearchText,
    }));
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (state.searchLoaded && selectedCostingTemplate) {
        setState((setSearchstate) => ({
          ...setSearchstate,
          hasMore: true,
          isLoading: true,
        }));

        const loadingObj = {
          loading: true,
        };
        await dispatch(filterSortData(loadingObj));

        const params = {
          ...costingFilterValues,
          pageIndex: 1,
          pageSize: Page_Size,
          type: 'costing',
          searchText: state.searchText,
          costingTemplateId: selectedCostingTemplate,
        } as CostingListParams;
        const obj = {
          ...costingFilterValues,
          pageIndex: 1,
          pageSize: Page_Size,
          searchText: state.searchText,
          type: 'costing',
        } as Partial<CostingFilterValues>;
        if (isSupplierLoggedIn) {
          params.supplierId = userInfo.userId;
        }

        try {
          await dispatch(filterSortData(obj));
          await dispatch(getCostingList(params));
          dispatch(getCostingStats(params));
        } finally {
          const afterApiCallObj = {
            loading: false,
          };
          await dispatch(filterSortData(afterApiCallObj));
          setState((resetSearchstate) => ({
            ...resetSearchstate,
            searchLoaded: false,
            isLoading: false,
          }));
        }
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchText, isSupplierLoggedIn]);

  useEffect(() => {
    return () => {
      /* The reset object deliberately omits isInitialLoad, matching the original. The cast
         keeps that shape rather than silently reintroducing the key. */
      setState({
        costingTodoList: [],
        isLoading: false,
        hasMore: false,
        loading: true,
        totalRecords: null,
        searchLoaded: false,
        isExportLoading: false,
        isDownloadLoading: false,
        selectedCount: 0,
            selectedIds: [],
        costingCommentListingModal: false,
        costingCommentList: [],
        selectedCostingTemplate: null,
        selectedColumns: [],
        searchText: '',
            selectedCostingWithDevids: [],
        isTabLoading: false,
      } as unknown as CostingPageState);

      dispatch(setCostingSelectedIds([]));

      dispatch(
        filterSortData({
          selectedcosting_Season: [],
          selectedcosting_Year: [],
          selectedcosting_Brand: [],
          selectedcosting_Supplier: [],
          selectedcosting_Type: '',
          selectedcosting_Category1: [],
          selectedcosting_Category2: [],
          selectedcosting_Category3: [],
          selectedcosting_Category4: [],
          selectedcosting_Section: '',
          selectedcosting_Status: [],
          searchText: '',
          pageIndex: 1,
          pageSize: Page_Size,
          sortOrder: '',
          sortPropertyName: '',
          loading: false,
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div>
        <div className="page-title-filter sticky costing-todo-titlebar">
          <div className="title-container d-flex align-items-center">
            <h4 className="title">{t('REPORTS.COSTING_TO_DO')}</h4>
          </div>
          <div className="search-filter">
            {state.selectedCount > 0 && (
              <span className="txt-selected-cnt">
                {state.selectedCount} {t('COSTING_TO_DO_LIST.RECORD_SELECTED')}
              </span>
            )}
            {costingListTotalCount > 0 && (
              <span className="d-none d-md-inline-block txt-selected-cnt">
                {costingListTotalCount} {t('COSTING_TO_DO_LIST.TOTAL_RECORDS')}
              </span>
            )}
            {/* Figma 5239:47369 — a permanently visible 200x28 field, replacing the magnifier that
                had to be clicked to expand. Styling is the shared `.bombiix-search` skin. */}
            <NewSearch
              className="bombiix-search"
              width={200}
              value={state.searchText}
              onChange={handleSearch}
              placeholder={t('DEVLOPMENT_ORDER.TABLE.TABLE_INDEX.SEARCH_PRODUCT_COSTING')}
              testId="costing-todo-search"
            />
            <div className="search-filter action-icons-container">
              <NewSelectMultiSelect<Option>
                isMulti
                showSearch
                name="costingTodoColumns"
                containerClassName="costing-todo-column-chooser"
                options={activeCostingtemplateColumns}
                value={selectedColumnOptions}
                placeholder={t('DEVLOPMENT_ORDER.TABLE.PRODUCT_COSTING_FILTER.SELECT_COLUMNS')}
                selectAllLabel={t('DEVLOPMENT_ORDER.TABLE.PRODUCT_COSTING_FILTER.SELECT_COLUMNS')}
                getOptionLabel={(option) => String(option?.label ?? '')}
                getOptionValue={(option) => String(option?.value ?? '')}
                onChange={(selected) => handleSelectedColumns((selected as Option[]) ?? [])}
              />
              <ProductCostingFilter activeCostingTemplate={selectedCostingTemplate} />
            </div>
          </div>
        </div>
      </div>
      <Container className="mt-24 pb-100" fluid>
        <div className="d-flex align-items-center">
          <ProductCostingTabMenu
            masterActiveTab={selectedCostingTemplate}
            MenuList={costingTemplateListOptions}
            isAPILoading={state.isTabLoading}
            activeTheTab={(val: string) => activeTheTab(val, 'selectedCostingTemplate')}
          />
        </div>

        {selectedCostingTemplate && (
          <CostingTODOTable
            onCostingsSaved={refreshCostingStats}
            loading={state.isLoading}
            getSelectedCount={getCount}
            getSelectedIdsWithDevIds={getSelectedIdsWithDevIds}
            activeCostingTemplate={selectedCostingTemplate}
            onClick={(type: string) => exportFiles(type)}
            isDownloadLoading={state?.isDownloadLoading}
            isExportLoading={state?.isExportLoading}
          />
        )}
        {costingTemplateListOptions?.length === 0 && <p className="text-center">{t('COSTING_TO_DO_LIST.NO_DATA_FOUND')}</p>}
      </Container>
    </>
  );
}

export default Costing;
